import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { deepPurple, pink as colorPink } from '@mui/material/colors';
import { keyframes } from '@mui/material/styles';
import * as React from 'react';
import { Box, FlexBox } from '../box';
import { IconButton } from '../button';
import { styled } from '../styled';
import { TextField } from '../textfield';
import { getPaletteColor } from '../theme';
import { withVersion } from '../utils';
const gradientShift = keyframes `
    0% {
        background-position: 0% 50%;
    }
    100% {
        background-position: 200% 50%;
    }
`;
const OUTER_RADIUS = 8;
const GRADIENT_BORDER_WIDTH = 2;
const GradientBorder = styled(Box)(({ $loading, theme }) => {
    const { 400: purple } = deepPurple;
    const { 400: pink } = colorPink;
    const primary = getPaletteColor(theme, 'primary.main');
    const GRADIENT = `linear-gradient(90deg, ${primary}, ${purple}, ${pink}, ${purple}, ${primary})`;
    return {
        boxSizing: 'border-box',
        borderRadius: OUTER_RADIUS,
        padding: $loading ? GRADIENT_BORDER_WIDTH : 0,
        background: $loading ? GRADIENT : 'transparent',
        backgroundSize: $loading ? '200% 100%' : undefined,
        animation: $loading ? `${gradientShift} 3s linear infinite` : undefined,
        '@media (prefers-reduced-motion: reduce)': {
            animation: $loading ? 'none' : undefined
        }
    };
});
const Root = styled(FlexBox)(({ theme, $disabled, $loading }) => ({
    boxSizing: 'border-box',
    borderRadius: $loading
        ? OUTER_RADIUS - GRADIENT_BORDER_WIDTH
        : OUTER_RADIUS - 1,
    outline: $loading
        ? 'none'
        : `1px solid ${getPaletteColor(theme, 'components.input.outlined.enabled-border')}`,
    backgroundColor: getPaletteColor(theme, 'background.paper'),
    flexDirection: 'column',
    '&:hover': $disabled || $loading
        ? {}
        : {
            outlineColor: getPaletteColor(theme, 'components.input.outlined.hover-border')
        },
    '&:focus-within': $disabled || $loading
        ? {}
        : {
            outlineColor: getPaletteColor(theme, 'primary.main'),
            boxShadow: `0px 0px 0px 4px ${getPaletteColor(theme, 'primary._states.focus-visible')}`
        }
}));
const TextAreaAIImpl = React.forwardRef(({ value = '', placeholder, disabled = false, loading = false, bottomContent, rows = 1, maxRows = 6, onChange, onSend, onStop, 'data-role': dataRole = 'text-area-ai', 'data-role-value': dataRoleValue, ...props }, ref) => {
    return (_jsx(GradientBorder, { "$loading": loading, "data-role": dataRole, "data-role-value": dataRoleValue, children: _jsxs(Root, { ref: ref, "$disabled": disabled, "$loading": loading, p: 2, ...props, children: [_jsx(Box, { flexGrow: 1, children: _jsx(TextField, { disabled: disabled, maxRows: maxRows, minRows: rows, multiline: true, onChange: onChange, placeholder: placeholder, slotProps: { input: { disableUnderline: true } }, value: value, variant: "standard" }) }), _jsxs(FlexBox, { gap: 1, hAlign: "space-between", vAlign: "flex-end", children: [_jsx(FlexBox, { flexGrow: 1, flexWrap: "wrap", gap: 1, children: bottomContent }), _jsx(FlexBox, { flexGrow: 0, flexShrink: 0, children: loading ? (_jsx(IconButton, { "aria-label": "Stop", color: "primary", icon: "IconStop", onClick: () => onStop?.(), size: "small", variant: "contained" })) : (_jsx(IconButton, { "aria-label": "Send", color: "primary", disabled: disabled, icon: "IconArrowForward", onClick: () => onSend?.(), size: "small", variant: "contained" })) })] })] }) }));
});
export const TextAreaAI = withVersion(TextAreaAIImpl, '1.0.0');
export default TextAreaAI;
